import type { TrackingRecoveryState } from '../../../interaction/camera/tracking-recovery';
import type {
  SemanticEventAnyListener,
  SemanticEventBus,
  SemanticEventListener,
  Unsubscribe,
} from '../../../interaction/events/semantic-event-bus';
import type { ThriveInputEvent, ThriveInputEventType } from '../../../shared/types/input-events';

/**
 * What this sitting's camera managed, counted so a person does not have to (CAM-08, Slice H).
 *
 * CAM-08 asks for two numbers about a real child in a real room: **at least 80% of deliberate
 * actions recognised after calibration**, and **fewer than three disruptive failures per session**.
 * Neither can be simulated — a synthetic body is recognised by construction — so Phase 3 owes the
 * measurement its *instrumentation* rather than its result (recon §A). This is that
 * instrumentation: the counts a grown-up would otherwise be tallying on paper while also watching
 * their five-year-old.
 *
 * **Three honesty rules are structural here, not conventions.**
 *
 *  1. *Only deliberate onsets count as actions.* Lowering your hands undoes an answer and walking
 *     back into shot is not an answer at all; counting either would inflate the very numerator
 *     CAM-08 is about.
 *  2. *The share is a share of what the camera formed an opinion about* — read movements over read
 *     plus knowingly-unread ones. A movement the detector never noticed is in neither count, which
 *     makes this an upper bound and is said out loud on the surface that shows it.
 *  3. *An episode is counted when it starts, once.* A mission held for four seconds is one wait,
 *     not four hundred frames of waiting, and coming back is not a second interruption.
 *
 * Nothing here is persisted, nothing here is child-facing, and nothing here is a fact about a
 * child: uncertainty is a detector saying it could not see, which D-059 PS3-1 keeps strictly apart
 * from a wrong answer.
 */

export interface CameraActivityTally {
  /**
   * Deliberate onsets the detectors were confident enough to emit, **while a round was running and
   * asking for that movement**.
   *
   * Both qualifications were added in fix round 1 (S1-8). A Full-Motion jump during a yes/no round
   * and an onset emitted behind the pause overlay are movements a child made; neither is an attempt
   * to answer the question on screen, and counting them made the CAM-08 numerator describe
   * something other than what CAM-08 asks about.
   */
  readonly recognisedActions: number;
  /** Times a movement was attempted and the camera could not read it (D-059 PS3-1). */
  readonly uncertaintyEpisodes: number;
  /** Times the mission was held because the camera could not see the child (GDS §36). */
  readonly lossEpisodes: number;
  /** Times a lasting problem was an interruption *of its own* (see {@link interruptionsBetween}). */
  readonly fallbackOffers: number;
  /**
   * Whether an unreadable movement has already been counted since the last one the camera read.
   *
   * The grouping S1-8 asked for, and the reason it is a field rather than a convention: the
   * uncertainty sink is called **once per capability per episode**, so a single idle frame at 0.59
   * confidence used to add five "movements the camera could not read" — presence, zone, jump, crouch
   * and hands — for a child who was standing still. One attempted movement is one entry, whichever
   * parts of the body the detectors were unsure about.
   */
  readonly uncertaintyOpen: boolean;
}

export const NO_CAMERA_ACTIVITY: CameraActivityTally = {
  recognisedActions: 0,
  uncertaintyEpisodes: 0,
  lossEpisodes: 0,
  fallbackOffers: 0,
  uncertaintyOpen: false,
};

/**
 * What the round on screen is currently asking a body to do (S1-8).
 *
 * The input layer's own vocabulary, deliberately: the adapter already speaks in `ZONES` / `BINARY`,
 * and inventing a second, finer classification here would be a second place for "what this round
 * wants" to be decided — and to drift.
 */
export interface CameraActivityContext {
  /** Whether a round is actually listening. Nothing counted behind a pause overlay. */
  readonly running: boolean;
  readonly inputContext: 'ZONES' | 'BINARY';
  /**
   * Whether a question is on screen waiting for an answer (S2-6).
   *
   * Distinct from `running`, and the distinction is the finding: a mission that is not paused is
   * very often not *asking* anything — the opening narrative beat, the moment after an answer while
   * the feedback plays, the gap between one round being resolved and the next being presented. A
   * child bouncing on the spot while Dr Spotty talks is not attempting to answer, and counting it
   * inflates the numerator of a recognition measurement.
   */
  readonly roundOpen: boolean;
}

/**
 * Which onsets are an attempt to answer *this* round.
 *
 * `ZONES` covers the lane and counting mechanics — stepping and jumping. `BINARY` is the yes/no
 * posture pair. A jump during a yes/no round is a five-year-old being five, not an answer.
 */
const RELEVANT_ACTIONS: Readonly<
  Record<CameraActivityContext['inputContext'], ReadonlySet<ThriveInputEventType>>
> = {
  ZONES: new Set<ThriveInputEventType>(['PLAYER_ZONE_CHANGED', 'PLAYER_JUMPED']),
  BINARY: new Set<ThriveInputEventType>(['PLAYER_CROUCHED', 'PLAYER_RAISED_HANDS']),
};

export function isRelevantAction(
  type: ThriveInputEventType,
  context: CameraActivityContext | null,
): boolean {
  if (!isAttemptWindow(context)) return false;
  return RELEVANT_ACTIONS[context.inputContext].has(type);
}

/** Whether a movement right now could be an attempt to answer a question (S2-6). */
export function isAttemptWindow(
  context: CameraActivityContext | null,
): context is CameraActivityContext {
  return context !== null && context.running && context.roundOpen;
}

/**
 * The onsets a child performs *in order to answer*.
 *
 * Written out rather than derived from the union so that a new semantic event has to be classified
 * by a person: silently defaulting a future action into "not an action" would quietly bias the
 * CAM-08 number downward and nothing would fail.
 */
const DELIBERATE_ACTIONS: ReadonlySet<ThriveInputEventType> = new Set<ThriveInputEventType>([
  'PLAYER_ZONE_CHANGED',
  'PLAYER_JUMPED',
  'PLAYER_CROUCHED',
  'PLAYER_RAISED_HANDS',
]);

export function isDeliberateAction(type: ThriveInputEventType): boolean {
  return DELIBERATE_ACTIONS.has(type);
}

export function withRecognisedAction(
  tally: CameraActivityTally,
  type: ThriveInputEventType,
): CameraActivityTally {
  if (!isDeliberateAction(type)) return tally;
  // A movement the camera read closes whatever unreadable attempt was open: the next one the
  // detectors cannot make out is a new attempt, not the same one still going.
  return { ...tally, recognisedActions: tally.recognisedActions + 1, uncertaintyOpen: false };
}

export function withUncertaintyEpisode(tally: CameraActivityTally): CameraActivityTally {
  if (tally.uncertaintyOpen) return tally;
  return { ...tally, uncertaintyEpisodes: tally.uncertaintyEpisodes + 1, uncertaintyOpen: true };
}

/**
 * The camera is reading the body confidently again, so the unreadable attempt is over (S2-6).
 *
 * The second close boundary, and the one that was missing: an attempt used to stay open until the
 * camera read a *deliberate action*, so two genuinely separate unreadable attempts either side of a
 * confident, still moment counted as one. Standing there being seen clearly is the end of "the
 * camera could not read that", whether or not anything answerable happened next.
 */
export function withUncertaintyCleared(tally: CameraActivityTally): CameraActivityTally {
  if (!tally.uncertaintyOpen) return tally;
  return { ...tally, uncertaintyOpen: false };
}

export function withLossEpisode(tally: CameraActivityTally): CameraActivityTally {
  return { ...tally, lossEpisodes: tally.lossEpisodes + 1 };
}

export function withFallbackOffer(tally: CameraActivityTally): CameraActivityTally {
  return { ...tally, fallbackOffers: tally.fallbackOffers + 1 };
}

/**
 * The "<3 disruptive failures" figure: the times the sitting actually stopped for the camera.
 *
 * Uncertainty is deliberately not in it. A movement the camera could not read costs a child a
 * repeat of the movement, not the mission — nothing pauses, nothing is scored, and the round is
 * still open. Counting it as disruptive would make the second CAM-08 number describe something
 * other than disruption.
 */
export function disruptiveInterruptions(tally: CameraActivityTally): number {
  return tally.lossEpisodes + tally.fallbackOffers;
}

/** The recognised share, or `null` when the camera has not yet had an opinion to report. */
export function recognisedShare(tally: CameraActivityTally): number | null {
  const observed = tally.recognisedActions + tally.uncertaintyEpisodes;
  if (observed === 0) return null;
  return tally.recognisedActions / observed;
}

/**
 * Which seats of this sitting the one camera figure covers (Slice G′, D-076(6)).
 *
 * The figures are sitting-wide by construction — the CAM-08 taps wrap the one adapter chain, and no
 * per-child count exists anywhere to render (D-081(1)). What a grown-up cannot tell from the numbers
 * alone is **whose play is in them**: a mixed sitting has a child answering with the keys, and a
 * keyboard answer is not camera activity.
 *
 * Computed from seats, in the `data-camera-heroes` register (D-083(1)): it names wiring, never
 * quality, never ability, and never a child.
 */
export type CameraQualityCoverage = 'EVERY_HERO' | 'CAMERA_HEROES_ONLY';

export function cameraQualityCoverage(
  seatedHeroCount: number,
  cameraHeroCount: number,
): CameraQualityCoverage {
  return cameraHeroCount < seatedHeroCount ? 'CAMERA_HEROES_ONLY' : 'EVERY_HERO';
}

/**
 * What the camera did over the **whole** sitting, rather than at the end of it (S7).
 *
 * The figures are cumulative — every movement the camera read, every time the mission waited — and
 * reading their coverage off the seats as they stand at mission end told two lies at once. A sitting
 * whose last camera lane fell back to keys lost its entire camera history from the parent summary,
 * at the moment it was most worth reporting: the grown-up deciding whether the camera suits their
 * family is looking at the sitting that made them turn it off. And a sitting that was mixed for half
 * its length could be labelled as covering every hero because it happened to end that way.
 *
 * Two facts are enough, and both are monotonic, which is what makes them honest about a history:
 * whether this sitting ever read a body at all, and whether every seated hero was on the camera for
 * the whole of it.
 */
export interface CameraCoverageHistory {
  /**
   * Whether the camera ever actually **read a body** in this sitting (S2-5).
   *
   * A body, not a seat. Wiring a lane is a fact about a configuration; a grown-up reading a
   * camera-quality block is being told something about what the camera managed with their child in
   * front of it, and a sitting whose children never appeared has nothing to tell them: the share is
   * "not measured yet" and the interruptions describe a camera nobody used. Set from the first
   * attributed body, which is the first moment there is anything true to report.
   */
  readonly cameraSeen: boolean;
  /** Whether, at any moment, a seated hero was not on the camera. */
  readonly everBelowFull: boolean;
}

export const NO_CAMERA_COVERAGE: CameraCoverageHistory = {
  cameraSeen: false,
  everBelowFull: false,
};

/**
 * One observation of who was on the camera, folded in.
 *
 * Seats only. It records whether every seated hero was on the camera at this moment and nothing
 * else — including, deliberately, the interval **before** any body has been read (S2-6): a sitting
 * that starts on the keys because the camera was refused, and wins a camera back mid-mission, spent
 * part of itself below full coverage and the summary has to say so. Discarding that interval on the
 * grounds that no body had been seen yet lost exactly the history it exists to keep.
 */
export function withCameraSeats(
  history: CameraCoverageHistory,
  seatedHeroCount: number,
  cameraHeroCount: number,
): CameraCoverageHistory {
  if (history.everBelowFull) return history;
  const belowFull =
    cameraQualityCoverage(seatedHeroCount, cameraHeroCount) === 'CAMERA_HEROES_ONLY';
  return belowFull ? { ...history, everBelowFull: true } : history;
}

/**
 * The camera read somebody's body (S2-5, S3-6).
 *
 * The one thing that makes a sitting worth reporting a camera figure for, and it is an observation
 * rather than a configuration: a lane is wired from a stored profile, but a body is read from a
 * child who actually stood in front of the lens.
 *
 * Folded from the **attribution** boundary — the adapter's `onBodyAttributed` — and not from a
 * semantic arrival event. A body can be attributed and still be unreadable: a dim room, a child
 * side-on, a partial occlusion. Those sittings are the ones a grown-up most needs the camera block
 * for, and keying it on the confidence-gated arrival dropped every one of their episodes.
 */
export function withCameraBodySeen(history: CameraCoverageHistory): CameraCoverageHistory {
  return history.cameraSeen ? history : { ...history, cameraSeen: true };
}

/**
 * The conservative reading: **every hero** only when every seated hero was on the camera for the
 * whole sitting, and the camera-heroes label the moment that stopped being true.
 *
 * Conservative on purpose. Over-claiming coverage invites a grown-up to read a number as describing
 * a child whose play is not in it; under-claiming only invites them to read the sentence that says
 * so. One of those mistakes is theirs to make, and the other is ours.
 */
export function coverageFromHistory(history: CameraCoverageHistory): CameraQualityCoverage {
  return history.everBelowFull ? 'CAMERA_HEROES_ONLY' : 'EVERY_HERO';
}

/**
 * The sitting's camera reading, as the parent summary reports it (MIN §21, GDS §33).
 *
 * Deliberately narrower than the running tally: a summary is read after the fact by somebody
 * deciding whether the camera suited their family, so it carries the share, the number of times the
 * mission had to wait, and what those two figures cover. Nothing here is per child, and nothing here
 * is persisted.
 */
export interface SittingCameraQuality {
  /** `null` when the camera never formed an opinion — "not measured yet", never a zero. */
  readonly recognisedShare: number | null;
  readonly interruptions: number;
  readonly coverage: CameraQualityCoverage;
}

export function sittingCameraQuality(
  tally: CameraActivityTally,
  coverage: CameraQualityCoverage,
): SittingCameraQuality {
  return {
    recognisedShare: recognisedShare(tally),
    interruptions: disruptiveInterruptions(tally),
    coverage,
  };
}

/** How many interruptions *began* between two states of the recovery ladder. */
export function interruptionsBetween(
  previous: TrackingRecoveryState,
  next: TrackingRecoveryState,
): { lossEpisodes: number; fallbackOffers: number } {
  const lossBegan = !previous.trackingLost && next.trackingLost;
  /**
   * A fallback offer inside a wait that is already being counted is **not a second wait** (S1-8).
   *
   * One continuous loss that lasts long enough to reach GDS §36's fourth rung is one interruption
   * with a fourth rung in it, and reporting it as two made the "<3 disruptive failures" figure
   * describe ladder transitions rather than disruption. An offer raised with no tracking loss behind
   * it — a machine that cannot keep up — is its own interruption and still counts.
   */
  const offerIsItsOwnInterruption =
    !previous.fallbackOffered && next.fallbackOffered && !next.trackingLost;
  return {
    lossEpisodes: lossBegan ? 1 : 0,
    fallbackOffers: offerIsItsOwnInterruption ? 1 : 0,
  };
}

/**
 * The camera adapter's bus, with a tap on the way past.
 *
 * A decorator rather than a subscription because the question is *which adapter emitted this*: the
 * keyboard is attached to the same bus for the whole of a camera sitting (CAM-09), and a keyboard
 * answer counted as a recognised camera movement would flatter the measurement exactly where it
 * matters. Everything else delegates, so the adapter is talking to the real bus.
 */
export function observeCameraActions(
  bus: SemanticEventBus,
  onAction: (type: ThriveInputEventType) => void,
  /**
   * What the round on screen is asking for, read at the moment of the event (S1-8). Absent means
   * nothing is counted at all, which is the honest default for a tap with no round behind it.
   */
  contextNow: () => CameraActivityContext | null = () => null,
): SemanticEventBus {
  return {
    subscribe<TType extends ThriveInputEventType>(
      type: TType,
      listener: SemanticEventListener<TType>,
    ): Unsubscribe {
      return bus.subscribe(type, listener);
    },
    subscribeAll(listener: SemanticEventAnyListener): Unsubscribe {
      return bus.subscribeAll(listener);
    },
    unsubscribe<TType extends ThriveInputEventType>(
      type: TType,
      listener: SemanticEventListener<TType>,
    ): void {
      bus.unsubscribe(type, listener);
    },
    emit(event: ThriveInputEvent): void {
      // Everything still reaches the bus; only the *measurement* is selective.
      if (isDeliberateAction(event.type) && isRelevantAction(event.type, contextNow())) {
        onAction(event.type);
      }
      bus.emit(event);
    },
    clear(): void {
      bus.clear();
    },
    listenerCount(type?: ThriveInputEventType): number {
      return bus.listenerCount(type);
    },
  };
}
